/**
 * queueFlush — LAYER 2 of the offline queue: how it drains.
 *
 * TWO CURES LIVE HERE (BUG-2026-07-14-VC559):
 *
 * 1. THE FLUSH NO LONGER READS THE LINK-LAYER FLAG. It used to open with
 *    `if (flushing || !navigator.onLine) return { synced: 0, failed: 0 }`. On the
 *    operator's device that flag was FALSE on a live network, so the queue never
 *    drained — not once, in eleven days (`last_sync_at: null`). The flush now
 *    consults the reachability PROBE, which asks the actual server.
 *
 * 2. A SKIPPED FLUSH IS NO LONGER INDISTINGUISHABLE FROM A CLEAN ONE. `{synced:0,
 *    failed:0}` was returned both when the flush REFUSED TO RUN and when it ran and
 *    found nothing to do. Every caller read the silence as success. A cycle that did
 *    not happen now says so, in `skipped` (D-33 R1).
 */

import { applyReplayFailure, applyPredecessorFailure } from './replayFailure'
import { isVerifiedAck } from './ackVerify'
import { isConfirmedOffline } from './reachability'
import { runQueueRecovery, type QueueRecoveryResult } from './queueRecovery'
import { predecessorVerdict } from './queueOrdering'
import { updateQueue, applyOutcomesById, outcomesStillDue, type CycleOutcome } from './queueStoreWrite'
import {
  readQueue,
  getHandler,
  isCritical,
  emitConflict,
  emitFlushResult,
  settleDeliveredTwins,
} from './queueStore'
import type { QueueEntry, FlushResult } from './offlineQueueTypes'

const FLUSH_INTERVAL_MS = 30_000
const QUEUE_EXPIRY_MS = 72 * 60 * 60 * 1000
// Dead-letter purge window for NON-CRITICAL entries. Criticals are NEVER purged —
// see queueStore.isCritical: nothing is ephemeral today, so this branch is dormant
// by configuration, and the rep's Discard action (queueExit) is the real exit.
const DEAD_LETTER_MAX_AGE_MS = 24 * 60 * 60 * 1000

let flushTimer: ReturnType<typeof setInterval> | null = null
let flushing = false

/** Expire a stale NON-critical entry. Returns true when this entry was expired. */
function expireIfStale(entry: QueueEntry, now: number): boolean {
  const age = now - new Date(entry.createdAt).getTime()
  if (age <= QUEUE_EXPIRY_MS || isCritical(entry.type)) return false
  entry.status = 'failed'
  entry.failureReason = 'expired'
  return true
}

/** An entry rescheduled by backoff (replayFailure.retryDelayMs) whose next attempt is not due yet. */
function notYetDue(entry: QueueEntry, now: number): boolean {
  return entry.nextAttemptAt !== undefined && Date.parse(entry.nextAttemptAt) > now
}

/**
 * Replay one pending entry. Returns its contribution to the cycle counters, and whether it
 * CHANGED the entry (only a changed entry's outcome is written back — see flushQueue).
 */
async function replayEntry(entry: QueueEntry): Promise<{ synced: number; failed: number; changed: boolean; authRequired: boolean }> {
  const handler = getHandler(entry.type)
  if (!handler) return { synced: 0, failed: 0, changed: false, authRequired: false } // no handler registered yet — leave pending

  try {
    const ack = await handler(entry.payload)
    // DELETE-ON-VERIFIED-ACK (FR-FE-IRONCLAD-TELEMETRY-OUTBOX). An entry leaves the
    // durable outbox only on POSITIVE confirmation the server took the write:
    //   - a verified ack ({verified:true}) is that proof                → prune;
    //   - a void return is the legacy completion signal from a handler that predates
    //     the ack contract (every non-v2 handler) — trusted for backward-compat → prune;
    //   - a DEFINED result that is NOT a verified ack is a bare 2xx with no confirmed
    //     id → RETAIN. We never delete a rep's only copy on an unconfirmed reply; the
    //     entry stays pending so a later cycle can confirm it (queueStore.isCritical
    //     keeps it visible + discardable in the meantime).
    if (ack === undefined || isVerifiedAck(ack)) {
      // The "synced sentinel": status=failed with retries still remaining, removed by
      // pruneQueue below.
      entry.status = 'failed'
      return { synced: 1, failed: 0, changed: true, authRequired: false }
    }
    return { synced: 0, failed: 0, changed: false, authRequired: false } // resolved but UNCONFIRMED — retained, not pruned
  } catch (err) {
    const outcome = applyReplayFailure(entry, err, isCritical(entry.type))
    if (outcome.conflict) emitConflict(outcome.conflict)
    // applyReplayFailure always writes the entry (status, retries and/or lastAttempt).
    return { synced: 0, failed: outcome.failed ? 1 : 0, changed: true, authRequired: outcome.authRequired === true }
  }
}

/** Drop synced sentinels + purge aged-out non-critical dead letters. */
function pruneQueue(queue: QueueEntry[], now: number): QueueEntry[] {
  return queue.filter(e => {
    // status='failed' + retries < maxRetries is the SYNCED sentinel — remove.
    if (e.status === 'failed' && e.retries < e.maxRetries) return false
    // Genuinely failed: purge NON-CRITICAL dead letters past the window. A critical
    // is NEVER purged automatically — it stays visible, retryable, and discardable.
    if (e.status === 'failed' && e.retries >= e.maxRetries && !isCritical(e.type)) {
      const age = now - new Date(e.createdAt).getTime()
      if (age > DEAD_LETTER_MAX_AGE_MS) return false
    }
    return true
  })
}

/**
 * Flush all pending entries.
 *
 * Skips ONLY when a probe has CONFIRMED the server is unreachable (burning five
 * retries against a genuinely dead network would pin a rep's report to `failed` in
 * 2.5 minutes). It does NOT skip on `navigator.onLine`, and it does NOT skip on an
 * `unknown` verdict — with no evidence we try, because trying is how you find out.
 */
export async function flushQueue(): Promise<FlushResult> {
  if (flushing) {
    return { synced: 0, failed: 0, skipped: 'already-flushing' }
  }
  if (isConfirmedOffline()) {
    const skipped: FlushResult = { synced: 0, failed: 0, skipped: 'offline-confirmed' }
    emitFlushResult(skipped)
    return skipped
  }

  flushing = true
  let synced = 0
  let failed = 0
  try {
    // A SNAPSHOT to iterate — never to write back. The handlers below await the network, and
    // any writer may change the stored queue meanwhile (an enqueue, a discard, a retry). Each
    // entry this cycle disposes of is recorded in `processed`; only those outcomes are applied,
    // by id, onto the CURRENT queue at the end (TASK-FE-OUTBOX-SERIALIZE-QUEUE-WRITES-2026-10-09).
    const queue = await readQueue()
    const processed: CycleOutcome[] = []
    const now = Date.now()

    // Only an entry this cycle actually CHANGED is recorded. One it merely looked at (waiting
    // behind a predecessor, no handler, unconfirmed ack) keeps whatever is stored now — a
    // concurrent discard may have pinned it meanwhile, and its stale copy must not undo that.
    for (const entry of queue) {
      if (entry.status !== 'pending') continue
      if (expireIfStale(entry, now)) {
        processed.push({ seen: 'pending', entry })
        failed++
        continue
      }
      // A dependent write (v8 behind its v2 engagement) — see queueOrdering.ts. While the
      // predecessor is in flight it waits, untouched (not an attempt, not a failure). Once
      // the predecessor is TERMINALLY failed the dependent is pinned failed + captured
      // instead of waiting forever; it is never sent without its predecessor.
      const verdict = predecessorVerdict(entry, queue)
      if (verdict.kind === 'failed') {
        applyPredecessorFailure(entry, verdict.predecessor, verdict.reasonCode, isCritical(entry.type))
        processed.push({ seen: 'pending', entry })
        failed++
        continue
      }
      if (verdict.kind === 'waiting') continue
      // Rescheduled by backoff: not an attempt, not a failure — it waits for its time.
      if (notYetDue(entry, now)) continue
      const outcome = await replayEntry(entry)
      if (outcome.changed) processed.push({ seen: 'pending', entry })
      synced += outcome.synced
      failed += outcome.failed
      // DELIVERED: an earlier pinned copy of this same write (a visit report refused online, then
      // re-sent) is resolved by it — landed for the rest of this cycle, pruned at its end. Only a
      // delivery does that; a failed replay leaves every copy where it is (queueStore). The settled
      // twins are entries this cycle CHANGED (from their pinned, `failed` state), so they are
      // recorded like any other outcome.
      if (outcome.synced > 0) {
        for (const twin of settleDeliveredTwins(queue, entry)) processed.push({ seen: 'failed', entry: twin })
      }
      // The session is gone (401 after the one re-handoff): every further entry would meet the
      // same 401. Offer nothing more this cycle; the entries stay pending for after the login.
      if (outcome.authRequired) break
    }

    // Always re-write: a dead-letter purge must happen every cycle even when no
    // pending entry transitioned, else zombies persist until one happens to flip.
    // Outcomes land on the CURRENT queue: an entry written meanwhile is kept, an entry that
    // left meanwhile stays gone, and the prune selects on what is stored NOW.
    await updateQueue(current => ({
      next: pruneQueue(applyOutcomesById(current, outcomesStillDue(current, processed)), Date.now()),
      result: undefined,
    }))
  } finally {
    flushing = false
  }

  const result: FlushResult = { synced, failed }
  emitFlushResult(result)
  return result
}

/**
 * Run a flush and surface its outcome. D-33 R1 — NOT fire-and-forget: a rejected
 * flush promise (e.g. an IndexedDB read failure) is reported as a failure instead of
 * vanishing into an unhandled rejection.
 */
function flushAndSurface(): void {
  flushQueue().catch((err: unknown) => {
    console.error('[offlineQueue] flush cycle threw — the queue did NOT drain', err)
    emitFlushResult({ synced: 0, failed: 1 })
  })
}

/**
 * Re-pend the RECOVERABLE stranded entries once, then flush — the app-load pass that
 * brings a rep's stranded backlog home. See queueRecovery.ts for the anti-storm rules.
 *
 * This is the ONE place `runQueueRecovery`'s injected flush seam is bound to the real
 * `flushQueue`, so the wiring below and any caller share identical behaviour.
 */
export function recoverFailedEntriesOnce(): Promise<QueueRecoveryResult> {
  return runQueueRecovery(flushQueue)
}

/**
 * Run ONE on-load recovery cycle (re-pend the recoverable stranded entries, then
 * flush) and report it HONESTLY.
 *
 * D-33 R1 — the three outcomes are distinguishable in the log, because "recovered
 * nothing" and "recovered 84 reports" looked identical for 13 days and that is the
 * whole bug. A cycle that re-pends nothing BECAUSE everything stranded is
 * deterministic says so; it is not silence.
 */
function recoverAndSurface(): void {
  recoverFailedEntriesOnce()
    .then(result => {
      if (result.rePended > 0) {
        // WARN, not info: an entry only needs recovering because a durable write was
        // stranded on the device. That is an abnormal condition worth seeing in a field
        // device's console, not routine chatter. (`no-console` allows warn/error only.)
        console.warn(
          `[offlineQueue] recovery re-pended ${result.rePended} stranded entr`
          + `${result.rePended === 1 ? 'y' : 'ies'} and flushed: `
          + `${result.flush.synced} synced, ${result.flush.failed} failed.`,
        )
      } else if (result.skippedDeterministic > 0) {
        console.warn(
          `[offlineQueue] recovery re-pended NOTHING: all ${result.skippedDeterministic} `
          + 'stranded entries failed deterministically (a replay cannot fix them). They '
          + 'need the rep\'s manual retry after correcting the input, or a Discard.',
        )
      }
    })
    .catch((err: unknown) => {
      console.error('[offlineQueue] recovery cycle threw — the backlog did NOT drain', err)
      emitFlushResult({ synced: 0, failed: 1 })
    })
}

/**
 * Start the background flush loop. Call once at app init.
 *
 * The interval no longer gates on `navigator.onLine` either — that was the second
 * copy of the same bug (offlineQueue.ts:283). `flushQueue` owns the decision, and it
 * makes it from the probe.
 *
 * THE APP-LOAD PASS RECOVERS; THE INTERVAL DOES NOT (BUG-FE-QUEUE-FAILED-NEVER-REPLAYS).
 * A 'failed' entry is skipped by every flush forever, so without a recovery pass the
 * rep's stranded backlog is unreachable by anything but a tap he was never prompted to
 * make. Recovery is wired HERE, and ONLY here, for two reasons:
 *   - `flushTimer` already makes this function idempotent, so recovery inherits
 *     once-per-load from a guard that exists — both call sites (AppLayout,
 *     useMorningSync) get it without knowing about it;
 *   - the 30s interval and the `online` listener keep the PLAIN flush. Re-pending on a
 *     timer would re-attempt a stranded entry every 30 seconds against a rep's battery
 *     and data plan — a retry storm, and the reason the original retry was manual.
 */
export function startOfflineQueue(): void {
  if (flushTimer) return // already running
  window.addEventListener('online', flushAndSurface)
  flushTimer = setInterval(flushAndSurface, FLUSH_INTERVAL_MS)
  recoverAndSurface()
}

/** Stop the background flush loop. */
export function stopOfflineQueue(): void {
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
  window.removeEventListener('online', flushAndSurface)
}
